import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { RefreshCw, Plus } from 'lucide-react'
import { useTasks } from '@/hooks/useTasks'
import { Button } from '@/components/ui/button'
import { StatusBadge } from '@/components/ui/status-badge'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { TASK_STATUS } from '@/lib/constants'
import { formatDateTime } from '@/lib/utils'
import type { TaskStatus } from '@/types'

export function TaskListPage() {
  const navigate = useNavigate()
  const { data: tasks, isLoading, refetch, isFetching } = useTasks()
  const [selectedStatuses, setSelectedStatuses] = useState<Set<TaskStatus>>(new Set())

  const toggleStatus = (status: TaskStatus) => {
    setSelectedStatuses(prev => {
      const next = new Set(prev)
      if (next.has(status)) next.delete(status)
      else next.add(status)
      return next
    })
  }

  const visibleTasks = tasks
    ? selectedStatuses.size > 0
      ? tasks.filter(t => selectedStatuses.has(t.status as TaskStatus))
      : tasks
    : []

  if (isLoading) return <LoadingSpinner className="mt-20" />

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Задания</h1>
          <p className="text-sm text-gray-500 mt-1">Список заданий на маркировку</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/admin/tasks/new')}>
            <Plus className="w-4 h-4 mr-1" /> Создать задание
          </Button>
          <Button variant="ghost" size="sm" onClick={() => refetch()} isLoading={isFetching}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap mb-4 items-center">
        <span className="text-xs text-gray-500 font-medium mr-1">Фильтр:</span>
        {(Object.entries(TASK_STATUS) as [TaskStatus, typeof TASK_STATUS[TaskStatus]][]).map(([status, meta]) => {
          const selected = selectedStatuses.has(status)
          const count = tasks?.filter(t => t.status === status).length ?? 0
          return (
            <button
              key={status}
              onClick={() => toggleStatus(status)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                selected
                  ? `${meta.color} ${meta.textColor} border-current ring-1 ring-current`
                  : 'bg-white text-gray-600 border-gray-200 hover:border-blue-400'
              }`}
            >
              {meta.label}
              <span className={`ml-1 ${selected ? 'opacity-80' : 'opacity-50'}`}>({count})</span>
            </button>
          )
        })}
        {selectedStatuses.size > 0 && (
          <button
            onClick={() => setSelectedStatuses(new Set())}
            className="px-3 py-1 rounded-full text-xs font-medium border bg-white text-gray-400 border-gray-200 hover:text-red-500 hover:border-red-300 transition-colors"
          >
            × Сбросить
          </button>
        )}
      </div>

      {!visibleTasks.length ? (
        <div className="text-center text-gray-500 py-20">
          {selectedStatuses.size > 0 ? (
            <p className="text-lg">Нет заданий с выбранными статусами</p>
          ) : (
            <>
              <p className="text-lg">Нет заданий</p>
              <p className="text-sm mt-1">Создайте первое задание</p>
            </>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Название</th>
                <th className="px-4 py-3">Комментарий</th>
                <th className="px-4 py-3">Статус</th>
                <th className="px-4 py-3">Товарная группа</th>
                <th className="px-4 py-3">Коды</th>
                <th className="px-4 py-3">Коробки</th>
                <th className="px-4 py-3">Палеты</th>
                <th className="px-4 py-3">Создано</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {visibleTasks.map(t => {
                const meta = TASK_STATUS[t.status]
                return (
                  <tr
                    key={t.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/admin/tasks/${t.uuid}`)}
                  >
                    <td className="px-4 py-3 font-mono text-xs">{t.id}</td>
                    <td className="px-4 py-3 font-medium">{t.name}</td>
                    <td className="px-4 py-3 text-gray-600 max-w-[14rem] truncate" title={t.comment || undefined}>
                      {t.comment?.trim() ? t.comment : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge color={meta.color} textColor={meta.textColor}>{meta.label}</StatusBadge>
                    </td>
                    <td className="px-4 py-3">
                      {t.product_group_code
                        ? <span className="font-mono text-xs text-gray-700">{t.product_group_code}</span>
                        : <span className="text-xs text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">{t.codes_count}</td>
                    <td className="px-4 py-3 text-center">{t.boxes_count}</td>
                    <td className="px-4 py-3 text-center">{t.pallets_count}</td>
                    <td className="px-4 py-3 text-gray-500">{formatDateTime(t.created_at)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
